import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiSend, FiChevronRight, FiX, FiTrendingUp } from "react-icons/fi";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { chatApi } from "../api/chat";
import { ChatMessage as ChatMessageType } from "../types";
import ChatMessage from "../components/chat/ChatMessage";
import JuliaActivity from "../components/chat/JuliaActivity";
import { CHARACTERS } from "../assets/characters";
import { dashboardApi, ScoreRhData } from "../api/dashboard";

interface PriorityGroup {
  id: string;
  tone: "urgent" | "soon" | "ok";
  count: number;
  label: string;
  items: { id: string; title: string; detail: string; route: string }[];
}

interface EphemeralNews {
  id: string;
  text: string;
}

const PRIORITIES: PriorityGroup[] = [
  {
    id: "urgent",
    tone: "urgent",
    count: 5,
    label: "actions urgentes",
    items: [
      {
        id: "u1",
        title: "Mettre à jour le DUERP",
        detail: "Document non actualisé depuis plus de 12 mois.",
        route: "/obligations",
      },
      {
        id: "u2",
        title: "Finaliser 2 entretiens professionnels",
        detail: "Échéance sous 7 jours.",
        route: "/employes",
      },
      {
        id: "u3",
        title: "Contrôler 3 contrats CDD",
        detail: "Dates de fin et motifs à sécuriser.",
        route: "/documents",
      },
    ],
  },
  {
    id: "soon",
    tone: "soon",
    count: 8,
    label: "actions à venir",
    items: [
      {
        id: "s1",
        title: "Préparer la visite médicale",
        detail: "3 collaborateurs concernés ce mois-ci.",
        route: "/employes",
      },
      {
        id: "s2",
        title: "Relancer les notes de frais",
        detail: "4 dossiers en attente de validation.",
        route: "/notes-de-frais-rh",
      },
    ],
  },
  {
    id: "ok",
    tone: "ok",
    count: 27,
    label: "obligations conformes",
    items: [
      {
        id: "o1",
        title: "Affichages obligatoires",
        detail: "À jour sur l’ensemble des sites.",
        route: "/obligations",
      },
      {
        id: "o2",
        title: "Registre unique du personnel",
        detail: "Complet et consultable.",
        route: "/employes",
      },
    ],
  },
];

const NEWS_FEED: EphemeralNews[] = [
  { id: "n1", text: "SMIC : pensez à vérifier vos minima conventionnels." },
  {
    id: "n2",
    text: "Index égalité : déclaration à anticiper si effectif ≥ 50.",
  },
  { id: "n3", text: "Entretiens professionnels : 2 échéances cette semaine." },
  { id: "n4", text: "DUERP : une mise à jour annuelle est recommandée." },
  { id: "n5", text: "Absences : 1 demande en attente de validation manager." },
];

const TONE_COLORS = {
  urgent: { bg: "#FDEAEA", border: "#E74C3C", text: "#C0392B" },
  soon: { bg: "#FEF5E7", border: "#F39C12", text: "#B9770E" },
  ok: { bg: "#E8F8EF", border: "#27AE60", text: "#1E8449" },
};

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [openPriority, setOpenPriority] = useState<string | null>("urgent");
  const [newsIndex, setNewsIndex] = useState(0);
  const [showProgression, setShowProgression] = useState(false);
  const [scoreRh, setScoreRh] = useState<ScoreRhData | null>(null);
  const [scoreLoading, setScoreLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const firstName = user?.first_name || "Utilisateur";
  const userName = user ? `${user.first_name} ${user.last_name}` : "Vous";

  const progressionScore = scoreRh?.score ?? null;
  const progressionModules = scoreRh?.modules ?? [];
  const recommendations = scoreRh?.recommendations ?? [];

  useEffect(() => {
    let cancelled = false;
    setScoreLoading(true);
    dashboardApi
      .scoreRh()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (data) setScoreRh(data);
      })
      .catch(() => {
        if (!cancelled) setScoreRh(null);
      })
      .finally(() => {
        if (!cancelled) setScoreLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (location.hash !== "#notifications") return;
    const timer = window.setTimeout(() => {
      document
        .getElementById("priorites")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setNewsIndex((i) => (i + 1) % NEWS_FEED.length);
    }, 4500);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 110)}px`;
    }
  }, [inputValue]);

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isTyping) return;

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      conversation_id: conversationId || "",
      role: "user",
      content: trimmed,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    try {
      const response = await chatApi.sendMessage(trimmed, conversationId);
      const d = response.data as {
        conversation_id?: string;
        conversation?: { id: string };
        message: ChatMessageType;
      };
      setConversationId(String(d.conversation_id || d.conversation?.id || ""));
      setMessages((prev) => [...prev, d.message]);
    } catch {
      toast.error("Impossible de contacter Julia pour le moment.");
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - var(--header-height))",
        padding: "1.25rem 1.5rem 1.5rem",
        background:
          "radial-gradient(ellipse 80% 50% at 20% 0%, rgba(14,165,233,0.08), transparent 55%), var(--color-bg)",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.35fr) minmax(260px, 0.65fr)",
          gap: "1.25rem",
          alignItems: "stretch",
          minHeight: "calc(100vh - var(--header-height) - 2.5rem)",
        }}
        className="dashboard-cdc-grid"
      >
        {/* Colonne principale : bureau de Julia */}
        <section
          style={{
            background: "var(--color-surface)",
            borderRadius: "1.25rem",
            border: "1px solid var(--color-border-light)",
            boxShadow: "0 10px 40px rgba(15, 28, 51, 0.06)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              padding: "1.35rem 1.5rem 0.5rem",
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              alignItems: "flex-start",
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--color-brand)",
                }}
              >
                Bureau de Julia
              </p>
              <h1
                style={{
                  margin: "0.35rem 0 0",
                  fontSize: "clamp(1.45rem, 2.4vw, 1.9rem)",
                  fontWeight: 750,
                  color: "var(--color-primary)",
                  letterSpacing: "-0.02em",
                }}
              >
                Bonjour {firstName}
              </h1>
            </div>
            <button
              type="button"
              onClick={() => setShowProgression(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.45rem",
                padding: "0.55rem 0.9rem",
                borderRadius: "999px",
                border: "1px solid rgba(14,165,233,0.35)",
                background: "var(--color-brand-light)",
                color: "var(--color-brand-dark)",
                fontWeight: 700,
                fontSize: "0.8rem",
                cursor: "pointer",
                fontFamily: "var(--font-family)",
              }}
            >
              <FiTrendingUp size={15} />
              Parcours de progression
            </button>
          </div>

          <div style={{ padding: "0 1.5rem 0.75rem", maxWidth: 560 }}>
            <p
              style={{
                margin: 0,
                color: "var(--color-text)",
                lineHeight: 1.65,
                fontSize: "0.98rem",
              }}
            >
              {scoreLoading
                ? "Analyse de votre conformité RH en cours…"
                : scoreRh?.label ||
                  "Complétez vos données pour obtenir un score RH représentatif."}{" "}
              {!scoreLoading && progressionScore !== null && (
                <strong style={{ color: "var(--color-brand-dark)" }}>
                  Score RH : {progressionScore} %
                </strong>
              )}
              <br />
              {recommendations.length > 0
                ? "Priorités calculées à partir de vos données :"
                : "Je suis à votre écoute pour avancer sur vos sujets RH."}
            </p>
            {recommendations.length > 0 && (
              <ol
                style={{
                  margin: "0.65rem 0 0",
                  paddingLeft: "1.15rem",
                  color: "var(--color-text-secondary)",
                  lineHeight: 1.55,
                  fontSize: "0.92rem",
                }}
              >
                {recommendations.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            )}
            <p
              style={{
                margin: "0.75rem 0 0",
                color: "var(--color-text-secondary)",
                fontSize: "0.9rem",
              }}
            >
              Par quoi souhaitez-vous commencer ? Je suis également à votre
              écoute.
            </p>
          </div>

          {/* Scène visuelle : Julia au bureau */}
          <div
            style={{
              position: "relative",
              flex: 1,
              minHeight: 220,
              marginTop: "0.25rem",
              background:
                "linear-gradient(180deg, transparent 0%, #F0F7FA 45%, #E8F3F7 100%)",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <img
              src="/illustrations/plants.svg"
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                left: "4%",
                bottom: "18%",
                width: 90,
                opacity: 0.9,
                pointerEvents: "none",
              }}
            />
            <img
              src="/illustrations/plants.svg"
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                right: "6%",
                bottom: "22%",
                width: 70,
                opacity: 0.75,
                transform: "scaleX(-1)",
                pointerEvents: "none",
              }}
            />
            <motion.img
              src={CHARACTERS.julia}
              alt="Julia à son bureau"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                width: "min(280px, 46%)",
                height: "auto",
                zIndex: 1,
                marginBottom: 8,
                filter: "drop-shadow(0 12px 24px rgba(15,28,51,0.12))",
              }}
            />
            {/* Plateau de bureau */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: "8%",
                right: "8%",
                bottom: 0,
                height: 28,
                background: "linear-gradient(180deg, #D4E4EC 0%, #C5D8E2 100%)",
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
          </div>

          {/* Historique chat compact */}
          {messages.length > 0 && (
            <div
              style={{
                margin: "0 1.25rem",
                maxHeight: 160,
                overflowY: "auto",
                background: "var(--color-bg)",
                borderRadius: 12,
                padding: "0.65rem",
              }}
            >
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  role={msg.role}
                  content={msg.content}
                  userName={userName}
                  metadata={msg.metadata}
                />
              ))}
              {isTyping && <JuliaActivity active />}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Zone de saisie sous le bureau */}
          <div style={{ padding: "0.85rem 1.25rem 1.15rem" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                minHeight: 52,
                background: "#fff",
                border: "1px solid var(--color-border)",
                borderRadius: 16,
                padding: "0.45rem 0.45rem 0.45rem 1rem",
                boxShadow: "0 4px 18px rgba(15, 28, 51, 0.06)",
              }}
            >
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Parlez à Julia… Ex. : Je souhaite recruter un technicien"
                rows={1}
                disabled={isTyping}
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  resize: "none",
                  fontFamily: "var(--font-family)",
                  fontSize: "0.92rem",
                  lineHeight: 1.5,
                  maxHeight: 110,
                  color: "var(--color-text)",
                }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!inputValue.trim() || isTyping}
                aria-label="Envoyer"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: inputValue.trim()
                    ? "var(--color-brand)"
                    : "var(--color-bg)",
                  color: inputValue.trim() ? "#fff" : "var(--color-text-muted)",
                  cursor: inputValue.trim() ? "pointer" : "default",
                }}
              >
                <FiSend size={16} />
              </button>
            </div>
          </div>
        </section>

        {/* Colonne droite : priorités + actualités éphémères */}
        <aside
          style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <div
            id="priorites"
            style={{
              background: "var(--color-surface)",
              borderRadius: "1.25rem",
              border: "1px solid var(--color-border-light)",
              padding: "1.1rem 1.15rem",
              boxShadow: "0 8px 28px rgba(15, 28, 51, 0.05)",
              scrollMarginTop: "1rem",
            }}
          >
            <h2
              style={{
                margin: "0 0 0.85rem",
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--color-primary)",
              }}
            >
              Priorités
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.55rem",
              }}
            >
              {PRIORITIES.map((group) => {
                const colors = TONE_COLORS[group.tone];
                const open = openPriority === group.id;
                return (
                  <div
                    key={group.id}
                    style={{
                      borderRadius: 12,
                      border: `1px solid ${colors.border}33`,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenPriority(open ? null : group.id)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.5rem",
                        padding: "0.7rem 0.8rem",
                        background: colors.bg,
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "var(--font-family)",
                        textAlign: "left",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: "0.86rem",
                          color: colors.text,
                        }}
                      >
                        {group.count} {group.label}
                      </span>
                      <FiChevronRight
                        size={16}
                        style={{
                          color: colors.text,
                          transform: open ? "rotate(90deg)" : "none",
                          transition: "transform 0.15s ease",
                        }}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          style={{ overflow: "hidden", background: "#fff" }}
                        >
                          {group.items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => navigate(item.route)}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                padding: "0.7rem 0.85rem",
                                border: "none",
                                borderTop:
                                  "1px solid var(--color-border-light)",
                                background: "transparent",
                                cursor: "pointer",
                                fontFamily: "var(--font-family)",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 650,
                                  fontSize: "0.84rem",
                                  color: "var(--color-text)",
                                }}
                              >
                                {item.title}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.78rem",
                                  color: "var(--color-text-secondary)",
                                  marginTop: 2,
                                }}
                              >
                                {item.detail}
                              </div>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 180,
              background:
                "linear-gradient(165deg, #0B3A4A 0%, #0F5F73 55%, #128A9A 100%)",
              borderRadius: "1.25rem",
              padding: "1.15rem",
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <img
              src={CHARACTERS.dashboardNews}
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                right: -6,
                bottom: -4,
                width: 130,
                opacity: 0.35,
                pointerEvents: "none",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                opacity: 0.8,
              }}
            >
              Fil du jour
            </p>
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                minHeight: 90,
              }}
            >
              <AnimatePresence mode="wait">
                <motion.p
                  key={NEWS_FEED[newsIndex].id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  style={{
                    margin: 0,
                    fontSize: "1.02rem",
                    lineHeight: 1.45,
                    fontWeight: 600,
                    maxWidth: "85%",
                  }}
                >
                  {NEWS_FEED[newsIndex].text}
                </motion.p>
              </AnimatePresence>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {NEWS_FEED.map((n, i) => (
                <span
                  key={n.id}
                  style={{
                    width: i === newsIndex ? 16 : 6,
                    height: 6,
                    borderRadius: 99,
                    background:
                      i === newsIndex ? "#fff" : "rgba(255,255,255,0.35)",
                    transition: "width 0.2s ease",
                  }}
                />
              ))}
            </div>
          </div>

          <div
            style={{
              background: "var(--color-surface)",
              borderRadius: "1.25rem",
              border: "1px solid var(--color-border-light)",
              padding: "0.9rem 1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.85rem",
            }}
          >
            <img
              src={CHARACTERS.dashboardExpert}
              alt=""
              aria-hidden
              style={{ width: 64, height: "auto" }}
            />
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  color: "var(--color-primary)",
                }}
              >
                Besoin d’un expert ?
              </div>
              <button
                type="button"
                onClick={() => navigate("/marketplace")}
                style={{
                  marginTop: 4,
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "var(--color-brand-dark)",
                  fontWeight: 650,
                  fontSize: "0.8rem",
                  cursor: "pointer",
                  fontFamily: "var(--font-family)",
                }}
              >
                Voir la marketplace →
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Modal parcours de progression */}
      <AnimatePresence>
        {showProgression && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(8, 18, 32, 0.45)",
              zIndex: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
            }}
            onClick={() => setShowProgression(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 480,
                background: "#fff",
                borderRadius: 20,
                padding: "1.35rem 1.4rem 1.5rem",
                boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "1.15rem",
                    color: "var(--color-primary)",
                  }}
                >
                  Parcours de progression
                </h3>
                <button
                  type="button"
                  onClick={() => setShowProgression(false)}
                  aria-label="Fermer"
                  style={{
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "var(--color-text-muted)",
                  }}
                >
                  <FiX size={20} />
                </button>
              </div>
              <p
                style={{
                  margin: "0 0 1rem",
                  color: "var(--color-text-secondary)",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                }}
              >
                Plus vous utilisez les modules JULIA, plus votre score de
                compétences RH progresse.
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "0.9rem 1rem",
                  borderRadius: 14,
                  background: "var(--color-brand-light)",
                  marginBottom: "1rem",
                }}
              >
                <img
                  src={CHARACTERS.dashboardProgress}
                  alt=""
                  aria-hidden
                  style={{ width: 72 }}
                />
                <div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted)",
                      fontWeight: 650,
                    }}
                  >
                    Score global
                  </div>
                  <div
                    style={{
                      fontSize: "1.6rem",
                      fontWeight: 800,
                      color: "var(--color-brand-dark)",
                    }}
                  >
                    {progressionScore !== null ? `${progressionScore} %` : "-"}
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gap: "0.65rem" }}>
                {progressionModules.map((mod) => (
                  <div key={mod.name}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.82rem",
                        marginBottom: 4,
                      }}
                    >
                      <span style={{ fontWeight: 650 }}>{mod.name}</span>
                      <span style={{ color: "var(--color-text-secondary)" }}>
                        {mod.score} %
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        background: "var(--color-border-light)",
                        borderRadius: 99,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${mod.score}%`,
                          height: "100%",
                          background: "var(--color-brand)",
                          borderRadius: 99,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
  @media (max-width: 960px) {
  .dashboard-cdc-grid {
  grid-template-columns: 1fr !important;
  min-height: auto !important;
  }
  }
  `}</style>
    </div>
  );
};

export default DashboardPage;
